import type { ChatItem, DateDivider } from './types';

/** Default grouping window in ms for adjacent messages from the same author. */
export const DEFAULT_GROUP_WINDOW_MS = 60_000;

/** Decoration knobs; groupWindowMs is ms and viewerTimeZone is an IANA zone id. */
export interface DecorationOptions {
  viewerTimeZone?: string;
  locale?: string;
  now?: Date;
  groupWindowMs?: number;
}

/**
 * Returns decorated rows with date dividers and grouping flags; input rows are not mutated.
 * Rows whose decoration is unchanged are returned by reference so downstream memoization
 * (React row memo, engine content checks) survives unrelated store mutations.
 */
export function decorateChatItems(
  messages: ChatItem[],
  options: DecorationOptions = {}
): ChatItem[] {
  const viewerTimeZone = options.viewerTimeZone ?? getDefaultTimeZone();
  const groupWindowMs = options.groupWindowMs ?? DEFAULT_GROUP_WINDOW_MS;
  const now = options.now ?? new Date();
  const todayKey = dateKeyFor(now, viewerTimeZone);
  const yesterdayKey = previousDateKey(todayKey);

  return messages.map((message, index) => {
    const previous = messages[index - 1];
    const dateKey = dateKeyFor(new Date(message.createdAt), viewerTimeZone);
    const previousDateKey = previous
      ? dateKeyFor(new Date(previous.createdAt), viewerTimeZone)
      : undefined;
    const dateDivider =
      !previousDateKey || dateKey !== previousDateKey
        ? makeDateDivider({
            date: new Date(message.createdAt),
            dateKey,
            todayKey,
            yesterdayKey,
            viewerTimeZone,
            locale: options.locale
          })
        : undefined;
    // This decoration is the single source of truth for both renderer grouping
    // and height estimation, so those consumers stay in lockstep.
    const groupedWithPrev =
      !dateDivider &&
      previous !== undefined &&
      previous.author.id === message.author.id &&
      authorType(previous) === authorType(message) &&
      isForwardWithinGroupWindow(previous, message, groupWindowMs);

    if (hasSameDecoration(message, dateDivider, groupedWithPrev)) return message;
    return { ...message, dateDivider, groupedWithPrev };
  });
}

function authorType(item: ChatItem): string {
  return item.author.type ?? 'profile';
}

function isForwardWithinGroupWindow(previous: ChatItem, message: ChatItem, groupWindowMs: number): boolean {
  const delta = new Date(message.createdAt).getTime() - new Date(previous.createdAt).getTime();
  return delta >= 0 && delta <= groupWindowMs;
}

function hasSameDecoration(
  item: ChatItem,
  dateDivider: DateDivider | undefined,
  groupedWithPrev: boolean
): boolean {
  if (item.groupedWithPrev !== groupedWithPrev) return false;

  const current = item.dateDivider;
  if (!current && !dateDivider) return true;
  if (!current || !dateDivider) return false;
  return (
    current.dateKey === dateDivider.dateKey &&
    current.label === dateDivider.label &&
    current.viewerTimeZone === dateDivider.viewerTimeZone
  );
}

/** Formats a date as YYYY-MM-DD in the viewer time zone, throwing if Intl cannot provide parts. */
export function dateKeyFor(date: Date, viewerTimeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: viewerTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;

  if (!year || !month || !day) {
    throw new Error(`could not compute date key for time zone ${viewerTimeZone}`);
  }

  return `${year}-${month}-${day}`;
}

function makeDateDivider(input: {
  date: Date;
  dateKey: string;
  todayKey: string;
  yesterdayKey: string;
  viewerTimeZone: string;
  locale?: string;
}): DateDivider {
  let label: string;
  if (input.dateKey === input.todayKey) {
    label = 'Today';
  } else if (input.dateKey === input.yesterdayKey) {
    label = 'Yesterday';
  } else {
    label = new Intl.DateTimeFormat(input.locale, {
      timeZone: input.viewerTimeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(input.date);
  }

  return {
    dateKey: input.dateKey,
    label,
    viewerTimeZone: input.viewerTimeZone
  };
}

function previousDateKey(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day) - 86_400_000).toISOString().slice(0, 10);
}

function getDefaultTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
